import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CaseItem } from '@/lib/caseTypes';
import SectionHeading from '@/components/base/SectionHeading';
import StatusBadge from '@/components/base/StatusBadge';

type Props = {
  items: CaseItem[];
};

const filters = ['全部', '持续追踪', '事件反转', '已澄清', '待核实'];

const RankSection = ({ items }: Props) => {
  const [active, setActive] = useState('全部');

  const list = useMemo(
    () => (active === '全部' ? items : items.filter((item) => item.status === active)),
    [active, items],
  );

  return (
    <section id="rank" className="scroll-mt-24 border-b border-background-200 bg-background-100">
      <div className="layout-shell py-12 md:py-16">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Tracking Board"
            title="热搜追踪榜"
            desc="按讨论热度排序，并标注每个事件的反转次数与当前状态。"
          />
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="按状态筛选追踪榜">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={active === f}
                onClick={() => setActive(f)}
                className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  active === f
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <ol className="mt-8 overflow-hidden rounded-lg border border-background-300/70 bg-background-50">
          {list.map((item, index) => (
            <li key={item.id} className="border-b border-background-200 last:border-b-0">
              <Link to={`/case/${item.id}`} className="group flex items-center gap-4 p-4 transition-colors hover:bg-background-100 md:gap-5 md:p-5">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-background-200 font-heading text-base font-bold text-foreground-800 md:h-10 md:w-10">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={item.status} />
                    <span className="text-xs text-foreground-500">{item.category}</span>
                    <span className="hidden text-xs text-foreground-500 sm:inline">· {item.handle}</span>
                  </div>
                  <h3 className="mt-2 line-clamp-1 font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                    {item.title}
                  </h3>
                </div>
                <div className="hidden flex-none items-center gap-6 text-xs text-foreground-500 md:flex">
                  <span className="flex items-center gap-1.5" title="反转次数">
                    <i className="ri-loop-left-line text-accent-600" aria-hidden="true" />
                    {item.reversal} 次
                  </span>
                  <span className="flex items-center gap-1.5" title="追踪人数">
                    <i className="ri-eye-line" aria-hidden="true" />
                    {item.follow}k
                  </span>
                  <span className="w-20 text-right">{item.updatedAt}</span>
                </div>
                <i className="ri-arrow-right-s-line flex-none text-foreground-400 transition-colors group-hover:text-primary-600" aria-hidden="true" />
              </Link>
            </li>
          ))}
          {list.length === 0 && (
            <li className="p-8 text-center text-sm text-foreground-500">该状态下暂无事件，试试切换其他筛选。</li>
          )}
        </ol>
      </div>
    </section>
  );
};

export default RankSection;